import React from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Image,
  ImageStyle,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Text from '../common/Text';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useAuth } from '../../hooks/useAuth';
import { RootStackParamList } from '../../types/navigation';
import { trackButtonClick } from '../../utils/analytics';

type NavProp = NativeStackNavigationProp<RootStackParamList>;

export type PublicScreen =
  | 'color-analysis'
  | 'virtual-drape'
  | 'palette-generator'
  | 'explore-colours'
  | null;

type Props = {
  activeScreen?: PublicScreen;
};

const PublicPageHeader: React.FC<Props> = ({
  activeScreen: _activeScreen = null,
}) => {
  const navigation = useNavigation<NavProp>();
  const { colors } = useTheme();
  const { isMobile } = useResponsiveLayout();
  const { isAuthenticated } = useAuth();
  const insets = useSafeAreaInsets();

  const goHome = () => {
    if (isAuthenticated) {
      trackButtonClick('Logo - Back to App', 'public-page-header', {
        button_type: 'navigation',
      });
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('MainTabs');
      }
    } else {
      trackButtonClick('Logo - Back to Landing', 'public-page-header', {
        button_type: 'navigation',
      });
      navigation.navigate('Landing');
    }
  };

  const goSignIn = () => {
    trackButtonClick('Sign In', 'public-page-header', {
      button_type: 'authentication',
    });
    navigation.navigate('Login');
  };

  const goSignUp = () => {
    trackButtonClick('Sign Up Free', 'public-page-header', {
      button_type: 'authentication',
    });
    navigation.navigate('Register');
  };

  return (
    <View
      style={[
        styles.header,
        {
          paddingTop: insets.top + 8,
          backgroundColor: colors.card,
          borderBottomColor: colors.border,
          ...(Platform.OS === 'web'
            ? ({
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
              } as object)
            : {}),
        },
      ]}
    >
      {/* Logo — tappable, navigates back to Landing */}
      <TouchableOpacity
        onPress={goHome}
        activeOpacity={0.8}
        style={styles.logoButton}
      >
        <Image
          source={require('../../../assets/coloursense_logo_with_name.png')}
          style={[styles.logo, isMobile && styles.logoMobile] as ImageStyle[]}
          resizeMode="contain"
        />
      </TouchableOpacity>

      {/* Right actions */}
      <View style={styles.actions}>
        {/* Sign In — desktop only */}
        {!isMobile && (
          <TouchableOpacity
            style={[styles.signInBtn, { borderColor: colors.border }]}
            onPress={goSignIn}
            activeOpacity={0.8}
          >
            <Text style={[styles.signInText, { color: colors.text }]}>
              Sign In
            </Text>
          </TouchableOpacity>
        )}

        {/* Sign Up — always visible */}
        <TouchableOpacity
          style={styles.signUpBtn}
          onPress={goSignUp}
          activeOpacity={0.8}
        >
          <Text style={styles.signUpText}>
            {isMobile ? 'Sign Up' : 'Sign Up Free →'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingBottom: 12,
    borderBottomWidth: 1,
    zIndex: 100,
  },
  logoButton: {
    flexShrink: 0,
  },
  logo: {
    width: 160,
    height: 40,
  },
  logoMobile: {
    width: 120,
    height: 32,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  signInBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1.5,
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
  },
  signInText: {
    fontSize: 14,
    fontWeight: '700',
  },
  signUpBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: '#9475e1',
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 2px 8px rgba(148,117,225,0.35)' } as object)
      : {
          shadowColor: '#9475e1',
          shadowOpacity: 0.35,
          shadowRadius: 8,
          shadowOffset: { width: 0, height: 2 },
          elevation: 4,
        }),
  },
  signUpText: {
    fontSize: 14,
    fontWeight: '800',
    color: '#FFFFFF',
    letterSpacing: 0.1,
  },
});

export default PublicPageHeader;
